/**
 * ReportRecommendations.js — Firewall rules, IR checklist, detection gaps
 */
import React, { useMemo } from "react";
import { getMitre, getPortInfo, IR_CHECKLIST, SEVERITY_COLORS } from "../reportUtils";

export default function ReportRecommendations({ stats, firewallRules, riskScore, theme, SEVERITY_COLORS: SC }) {
  if (!stats) return null;

  // Determine dominant severity for IR checklist
  const dominantSev = stats.critical > 0 ? "critical"
    : stats.high > 0 ? "high"
    : stats.medium > 0 ? "medium" : "low";

  const checklist = IR_CHECKLIST[dominantSev] || IR_CHECKLIST.medium;

  // Detection gap analysis
  const gapAnalysis = useMemo(() => {
    const gaps = [];
    if (!stats.timeCol)   gaps.push({ gap: "No timestamp data detected", impact: "Cannot perform temporal analysis or determine time of attack", recommendation: "Ensure logs include datetime/timestamp column", severity: "high" });
    if (!stats.srcIPCol)  gaps.push({ gap: "No source IP column detected", impact: "Cannot identify attacker IP addresses or perform IP reputation analysis", recommendation: "Include src_ip or source_address field in dataset", severity: "critical" });
    if (!stats.dstIPCol)  gaps.push({ gap: "No destination IP column detected", impact: "Cannot identify targeted systems", recommendation: "Include dst_ip or dest_ip field in dataset", severity: "high" });
    if (!stats.protoCol)  gaps.push({ gap: "No protocol column detected", impact: "Cannot perform protocol-level analysis", recommendation: "Include proto or protocol field", severity: "medium" });
    if (stats.srcIPDist.length === 0) gaps.push({ gap: "No valid IP addresses found", impact: "Network attribution impossible", recommendation: "Verify dataset contains real network traffic data", severity: "high" });
    if (stats.attackDist.length <= 1) gaps.push({ gap: "Single or no attack type detected", impact: "Limited threat diversity analysis", recommendation: "Expand dataset to include multiple traffic types", severity: "medium" });
    if (!stats.bytesCol)  gaps.push({ gap: "No bytes/payload size data", impact: "Cannot assess data exfiltration volume", recommendation: "Include bytes or payload_size field", severity: "low" });
    return gaps;
  }, [stats]);

  return (
    <div className="report-section page-break" style={{ marginBottom: 24 }}>
      <SectionTitle color="#2E7D32">Recommendations</SectionTitle>

      {/* Firewall Rules */}
      <div className="report-card" style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, padding: 20, marginBottom: 16 }}>
        <SubTitle theme={theme}>Recommended Firewall Rules</SubTitle>
        {firewallRules.length === 0 ? (
          <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted }}>Insufficient IP/port data to generate firewall rules</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "monospace", fontSize: 11 }}>
            <thead>
              <tr style={{ background: theme.colors.tableHeader }}>
                {["Rule ID","Action","Direction","Source","Destination","Port","Protocol","Reason"].map(h => (
                  <th key={h} style={{ padding: "7px 8px", textAlign: "left", color: theme.colors.textPrimary, borderBottom: `2px solid ${theme.colors.cardBorder}`, fontSize: 9, letterSpacing: 1 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {firewallRules.map((r, i) => {
                const actionColor = r.action === "DENY" || r.action === "DROP" ? SC.critical : SC.medium;
                return (
                  <tr key={i} style={{ background: i % 2 === 0 ? theme.colors.rowAlt : theme.colors.card, borderBottom: `1px solid ${theme.colors.cardBorder}` }}>
                    <td style={{ padding: "6px 8px", color: theme.colors.accent, fontWeight: 700 }}>{r.id}</td>
                    <td style={{ padding: "6px 8px" }}>
                      <span style={{ padding: "2px 5px", background: `${actionColor}18`, border: `1px solid ${actionColor}44`, borderRadius: 3, color: actionColor, fontSize: 9, fontWeight: 700 }}>{r.action}</span>
                    </td>
                    <td style={{ padding: "6px 8px", color: theme.colors.textMuted }}>{r.direction}</td>
                    <td style={{ padding: "6px 8px", color: "#C62828", fontFamily: "monospace" }}>{r.src}</td>
                    <td style={{ padding: "6px 8px", color: theme.colors.textPrimary }}>{r.dst}</td>
                    <td style={{ padding: "6px 8px", color: theme.colors.accent, fontWeight: 700 }}>{r.port}</td>
                    <td style={{ padding: "6px 8px", color: theme.colors.textMuted }}>{r.protocol}</td>
                    <td style={{ padding: "6px 8px", color: theme.colors.textMuted, fontSize: 9 }}>{r.reason}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* IR Checklist */}
        <div className="report-card" style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, padding: 20 }}>
          <SubTitle theme={theme}>Incident Response Checklist — {dominantSev.toUpperCase()} Priority</SubTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {checklist.map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 10px", background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 6 }}>
                <div style={{
                  width: 18, height: 18, border: `2px solid ${dominantSev === "critical" ? SC.critical : dominantSev === "high" ? SC.high : SC.medium}`,
                  borderRadius: 3, flexShrink: 0, marginTop: 1
                }} />
                <span style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textPrimary, lineHeight: 1.5 }}>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Detection Gap Analysis */}
        <div className="report-card" style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, padding: 20 }}>
          <SubTitle theme={theme}>Detection Gap Analysis</SubTitle>
          {gapAnalysis.length === 0 ? (
            <div style={{ padding: 16, background: "rgba(46,125,50,0.08)", border: "1px solid rgba(46,125,50,0.25)", borderRadius: 8, fontFamily: "monospace", fontSize: 12, color: "#2E7D32" }}>
              ✓ No significant data gaps detected — dataset appears comprehensive
            </div>
          ) : gapAnalysis.map((g, i) => {
            const color = SC[g.severity] || SC.info;
            return (
              <div key={i} style={{ marginBottom: 12, padding: "10px 12px", background: `${color}08`, border: `1px solid ${color}33`, borderRadius: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <span style={{ fontFamily: "monospace", fontSize: 11, fontWeight: 700, color: theme.colors.textPrimary }}>{g.gap}</span>
                  <span style={{ padding: "1px 6px", background: `${color}18`, border: `1px solid ${color}44`, borderRadius: 3, color, fontSize: 9, fontWeight: 700 }}>{g.severity.toUpperCase()}</span>
                </div>
                <div style={{ fontFamily: "monospace", fontSize: 11, color: "#EF9A9A", marginBottom: 5 }}>⚠ Impact: {g.impact}</div>
                <div style={{ fontFamily: "monospace", fontSize: 11, color: "#81C784", fontWeight: 600 }}>✓ Fix: {g.recommendation}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const SectionTitle = ({ children, color }) => (
  <div style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 900, color, letterSpacing: 2, textTransform: "uppercase", borderLeft: `4px solid ${color}`, paddingLeft: 12, marginBottom: 16 }}>{children}</div>
);
const SubTitle = ({ children, theme }) => (
  <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 10, fontWeight: 700 }}>{children}</div>
);
